import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

/**
 * Count up from 0 to a numeric value on scroll-into-view.
 * Reduced-motion / no-JS friendly: renders the final value in text immediately
 * (via aria-label + fallback text). Only fires once.
 *
 * Props:
 * - value: number (e.g. 90000)
 * - suffix: string appended after the number (e.g. "+")
 * - prefix: optional string prepended (e.g. "10+ ")
 * - unit: optional string after suffix (e.g. " years")
 * - duration: ms
 * - format: "compact" (10,000) | "raw" — defaults compact for values >=1000
 */
export default function Counter({
  value = 0,
  suffix = "",
  prefix = "",
  unit = "",
  duration = 1400,
  ariaFinal
}) {
  const [display, setDisplay] = useState(value);
  const started = useRef(false);
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return;
    }
    setDisplay(0);
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const from = 0;
            const to = value;
            const step = (now) => {
              const elapsed = now - start;
              const t = Math.min(1, elapsed / duration);
              const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
              setDisplay(Math.round(from + (to - from) * eased));
              if (t < 1) requestAnimationFrame(step);
              else setDisplay(to);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.35 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [value, duration, reduced]);

  const formatted = display.toLocaleString("en-IN");
  const rendered = `${prefix}${formatted}${suffix}${unit}`;

  return (
    <span
      ref={ref}
      className="tabular-nums"
      aria-label={ariaFinal || `${prefix}${value.toLocaleString("en-IN")}${suffix}${unit}`}
    >
      {rendered}
    </span>
  );
}
